import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@strivelabs/ui-core';
import { Popover } from '@strivelabs/ui-core/v2';
import { useDictationSettingsStore } from '@/store/dictationSettingsStore';

interface AudioInput {
  deviceId: string;
  label: string;
}

/**
 * Caret next to the mic that lists audio input devices and remembers the
 * chosen one. Enumerates devices lazily when opened.
 */
export const DictationDeviceMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<AudioInput[]>([]);
  const deviceId = useDictationSettingsStore((state) => state.deviceId);
  const setDeviceId = useDictationSettingsStore((state) => state.setDeviceId);

  const loadDevices = async () => {
    const all = await navigator.mediaDevices.enumerateDevices();
    setDevices(
      all
        .filter((device) => device.kind === 'audioinput')
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${index + 1}`,
        })),
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) void loadDevices();
      }}
      aria-label="Choose microphone"
      contentClassName="w-56 p-1.5"
      trigger={
        <button
          type="button"
          aria-label="Choose microphone"
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'flex h-6 w-0 items-center justify-center overflow-hidden rounded-md text-muted-foreground opacity-0 transition-[width,opacity,background-color] duration-150 hover:bg-accent hover:text-foreground group-hover:w-6 group-hover:opacity-100',
            open && 'w-6 opacity-100',
          )}
        >
          <ChevronDown className="size-3.5" />
        </button>
      }
    >
      {devices.map((device) => {
        const selected = device.deviceId === deviceId;
        return (
          <button
            key={device.deviceId}
            type="button"
            role="menuitem"
            aria-current={selected ? 'true' : undefined}
            onClick={() => {
              setDeviceId(device.deviceId);
              setOpen(false);
            }}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm outline-none transition-colors hover:bg-accent focus-visible:bg-accent',
              selected && 'font-medium text-foreground',
            )}
          >
            <span className="truncate">{device.label}</span>
            {selected && <Check className="size-4 shrink-0" />}
          </button>
        );
      })}
    </Popover>
  );
};
